/**
 * @file JSDoc type definitions for Promotion API
 * Generated from CreatePromotionRequest Swagger specification
 */

/**
 * @typedef {'PACE_ADAPTER' | 'ADMIN_UI' | 'ADMIN_BULK_UPLOAD' | 'CEP_SYNC'} PromotionSource
 * Source of the promotion
 */

/**
 * Runtime map of PromotionSource values for code reuse.
 * Use `PromotionSourceMap` to reference source keys/values at runtime.
 * @type {Object.<string, string>}
 */
export const PromotionSource = Object.freeze({
  PACE_ADAPTER: 'PACE_ADAPTER',
  ADMIN_UI: 'ADMIN_UI',
  ADMIN_BULK_UPLOAD: 'ADMIN_BULK_UPLOAD',
  CEP_SYNC: 'CEP_SYNC',
})

/**
 * @typedef {'VARIABLE' | 'FIXED'} ConditionType
 * Type of condition (Used in India market)
 */

/**
 * @typedef {'AND' | 'OR'} CombineOperator
 * Operator to combine conditions with
 */

/**
 * @typedef {'PRODUCT_ID' | 'GTIN'} ItemType
 * Type of item to buy
 */

/**
 * @typedef {'PRODUCT_ID' | 'PRODUCT_LINE' | 'PRODUCT_CONTAINER' | 'PRODUCT_GROUP' | 'PRODUCT_SIZE' | 'CATEGORY' | 'SUBCATEGORY' | 'BRAND' | 'SUBBRAND' | 'GTIN'} ExcludedItemType
 * Type of item to exclude
 */

/**
 * @typedef {'FREE_PRODUCT' | 'POINTS' | 'DISCOUNT' | 'GIFT_ITEM'} RewardType
 * Type of the reward
 */

/**
 * @typedef {'PERCENTAGE' | 'AMOUNT' | 'FIXED_AMOUNT'} DiscountType
 * Type of discount
 */

/**
 * Runtime map of DiscountType values for code reuse.
 * @type {Object.<string, string>}
 */
export const DiscountType = Object.freeze({
  PERCENTAGE: 'PERCENTAGE',
  AMOUNT: 'AMOUNT',
  FIXED_AMOUNT: 'FIXED_AMOUNT',
})

/**
 * @typedef {'GROSS' | 'MRP'} DiscountAppliedOn
 * On what to apply the discount
 */

/**
 * @typedef {'VOUCHER'} GiftItemType
 * Type of gift item
 */

/**
 * @typedef {'SEGMENT_LIST' | 'SEGMENT_FOLDER'} SegmentSelectionType
 * Type of segment configuration selected from UI
 */

/**
 * @typedef {'static' | 'clickable'} BannerType
 * Type of banner
 */

/**
 * @typedef {'DRAFT' | 'PUBLISHED'} PromotionStatus
 * Status of the promotion
 */

/**
 * @typedef {Object} ItemToBuy
 * @property {string} id - ID of the item to buy
 * @property {string} name - Name of the item to buy
 * @property {string} uom - UOM of the item to buy
 */

/**
 * @typedef {Object} RewardItem
 * @property {string} id - ID of the item
 * @property {string} name - Name of the item
 */

/**
 * @typedef {Object} Condition
 * A condition
 * @property {number|null} [quantity] - Quantity to buy
 * @property {number[]|null} [layeredQuantity] - Layered quantities to buy - number of layer should match with reward
 * @property {number|null} [amount] - Amount to buy
 * @property {number[]|null} [layeredAmount] - Layered amounts to buy - number of layer should match with reward
 * @property {number|null} [minimumOrderValue] - Minimum order value to apply the promotion
 * @property {ItemType} itemType - Type of item to buy
 * @property {ItemToBuy[]} items - Items to buy
 * @property {boolean} [productCombination] - Whether the products can be combined
 */

/**
 * @typedef {Object} ConditionGroup
 * A group of conditions
 * @property {CombineOperator} [combinedWith='AND'] - Operator to combine conditions with
 * @property {Condition[]} [conditions=[]] - List of conditions
 */

/**
 * @typedef {Object} DiscountReward
 * A discount reward
 * @property {'DISCOUNT'} type - Type of the reward
 * @property {DiscountType} discountType - Type of discount
 * @property {number} [discountValue] - Value of the discount
 * @property {number[]} [layeredDiscountValue] - Values of the discount for each layer
 * @property {number|null} [discountAmountUpto] - Amount up to which the discount is applicable
 * @property {number[]|null} [layeredDiscountAmountUpto] - Amount up to which the discount is applicable for each layer
 * @property {DiscountAppliedOn} [discountAppliedOn='GROSS'] - On what to apply the discount
 */

/**
 * @typedef {Object} FreeProductReward
 * A free product reward
 * @property {'FREE_PRODUCT'} type - Type of the reward
 * @property {number} [quantity] - Quantity of free products
 * @property {number[]} [layeredQuantity] - Quantity of free products for each layer
 * @property {ItemType} itemType - Type of item to free product
 * @property {RewardItem[]} items - Items to free product
 */

/**
 * @typedef {Object} GiftItemReward
 * A gift item reward
 * @property {'GIFT_ITEM'} type - Type of the reward
 * @property {number|null} [quantity] - Quantity of gift items
 * @property {number[]|null} [layeredQuantity] - Quantity of gift items for each layer
 * @property {GiftItemType[]} items - Items of gift items
 */

/**
 * @typedef {Object} PointsReward
 * A points reward
 * @property {'POINTS'} type - Type of the reward
 * @property {number} [points] - Number of points
 * @property {number[]} [layeredPoints] - Number of points for each layer
 */

/**
 * @typedef {DiscountReward | FreeProductReward | GiftItemReward | PointsReward} Reward
 * A reward
 */

/**
 * @typedef {Object} PromotionCondition
 * A condition for a promotion
 * @property {ConditionType} [conditionType] - Type of condition (Used in India market)
 * @property {ConditionGroup} [conditionGroup] - A group of conditions
 * @property {Reward[]} reward - Rewards for the condition
 */

/**
 * @typedef {Object} PromotionConditionWrapper
 * Condition and reward for the promotion
 * @property {PromotionCondition} [condition] - Promotion condition details
 * @property {Reward} [reward] - Reward details
 */

/**
 * @typedef {Object} ExcludedItemDetails
 * @property {string} [id] - ID of the item to exclude
 * @property {string} [name] - Name of the item to exclude
 */

/**
 * @typedef {Object} ExcludedItem
 * Items excluded from the promotion
 * @property {ExcludedItemType} [itemType] - Type of item to exclude
 * @property {ExcludedItemDetails} [items] - Items to exclude
 */

/**
 * @typedef {Object} SegmentDetails
 * Details of a segment
 * @property {string} [segmentId] - Segment IDs
 * @property {string} [segmentName] - Segment name
 * @property {string} [segmentFolderId] - Segment folder ID
 */

/**
 * @typedef {Object} SegmentConfiguration
 * Configuration for customer segments
 * @property {SegmentSelectionType} [selectionType='SEGMENT_LIST'] - Type of segment configuration selected from UI
 * @property {SegmentDetails[]} segments - Selected segments within same group
 */

/**
 * @typedef {Object} SegmentConfigurationWrapper
 * Segment configuration for the promotion
 * If allCustomers is true, then includedSegments is not required
 * Mandatory if allCustomers is false
 * @property {boolean} [allCustomers=false] - Whether all customers are eligible for the promotion
 * @property {SegmentConfiguration[]} [includedSegments] - Included segments for the promotion
 * @property {SegmentConfiguration} [excludedSegment] - Excluded segments for the promotion
 */

/**
 * @typedef {Object} LocalizedText
 * Localized text for a promotion
 * @property {string} [title] - Localized title
 * @property {string} [bannerTag] - Localized banner tag
 * @property {string} [productTag] - Localized product tag
 */

/**
 * @typedef {Object.<string, LocalizedText>} TextLocalization
 * Localized text for the promotion
 * Key is the language code (e.g., 'en', 'es', 'fr')
 */

/**
 * @typedef {Object} CreatePromotionRequest
 * Data transfer object for creating or updating a promotion
 * @property {string} [name] - Name of the promotion
 * @property {PromotionSource} [source] - Source of the promotion
 * @property {string} [sourceRefId] - Source reference ID for the promotion
 * @property {PromotionConditionWrapper} [promotionCondition] - Condition and reward for the promotion
 * @property {ExcludedItem[]} [excludedItems] - Items excluded from the promotion
 * @property {SegmentConfigurationWrapper} [segmentConfiguration] - Segment configuration for the promotion
 * @property {string} [bannerUrl] - URL for the promotion banner
 * @property {BannerType} [bannerType='static'] - Type of banner
 * @property {string} [landingPage] - Landing page for the promotion when bannerType is clickable
 * @property {string} [routeTypeId] - Route type ID for the promotion to support clickable banner
 * @property {string[]} [displayOn] - Pages to display the promotion on
 * @property {number} [displayOrder] - Order to display the promotion in
 * @property {TextLocalization} [text] - Localized text for the promotion
 * @property {string} [activeStartDate] - Start date and time for the promotion (UTC) - ISO 8601 format
 * @property {string} [activeEndDate] - End date and time for the promotion (UTC) - ISO 8601 format
 * @property {boolean} [active] - Whether the promotion is active
 * @property {number} [maxAllowedOrderPerStore] - Maximum number of orders per store
 * @property {number} [maxFreeItemsPerOrder] - Maximum number of free items per order (for GIFT_ITEM and FREE_PRODUCT reward types)
 * @property {number} [maxTimesPerOrder] - Maximum times the promotion can be applied per order (for POINTS reward type)
 * @property {number} [maxAllowedPercentage] - The maximum allowed percentage of the reward (for DISCOUNT reward type)
 * @property {number} [resetFrequencyInDays] - Frequency in days to reset the promotion
 * @property {string} [behavior] - Behavior of the promotion
 * @property {string} [externalPromotionId] - External ID for the promotion
 * @property {string} [benefitExternalId] - External ID for the benefit
 * @property {string} [conditionExternalId] - External ID for the condition
 * @property {string} [externalId] - External ID
 * @property {PromotionStatus} status - Status of the promotion (required)
 * @property {string} [requestedBy] - Email of the user who requested the promotion
 */

export {}
